import type { components } from "@brandex/api-client";
import { useBrandex, useMe } from "@brandex/dashboard";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

export type PlatformStats = components["schemas"]["PlatformStatsDto"];
export type AdminOrganization = components["schemas"]["AdminOrganizationDto"];
export type AdminUser = components["schemas"]["UserDto"];
export type AuditEvent = components["schemas"]["AuditEventDto"];

const PAGE = 25;

function required<T>(data: T | undefined): T {
  if (data === undefined) throw new Error("Empty response body");
  return data;
}

export function useStats() {
  const { api } = useBrandex();
  return useQuery({
    queryKey: ["admin", "stats"],
    queryFn: async () => required((await api.http.GET("/v1/admin/stats")).data),
    refetchInterval: 60_000,
  });
}

type ListPath = "/v1/admin/organizations" | "/v1/admin/users" | "/v1/admin/audit-events";

/** Cursor-paginated staff listing ("Load more"). */
function useAdminList<T>(path: ListPath) {
  const { api } = useBrandex();
  return useInfiniteQuery({
    queryKey: ["admin", path],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data } = await api.http.GET(path, {
        params: { query: { limit: PAGE, ...(pageParam === undefined ? {} : { cursor: pageParam }) } },
      });
      return required(data) as { items: T[]; next_cursor?: string | null };
    },
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    select: (d) => d.pages.flatMap((p) => p.items),
  });
}

export const useOrganizations = () => useAdminList<AdminOrganization>("/v1/admin/organizations");
export const useUsers = () => useAdminList<AdminUser>("/v1/admin/users");
export const useAuditEvents = () => useAdminList<AuditEvent>("/v1/admin/audit-events");

// ------------------------------------------------------------ commerce (F-04)

export type Component = components["schemas"]["ComponentDto"];
export type AdminPlan = components["schemas"]["AdminPlanDto"];
export type Quote = components["schemas"]["QuoteDto"];
export type Subscription = components["schemas"]["SubscriptionDto"];
export type ChangeRequest = components["schemas"]["ChangeRequestDto"];
export type ComponentRequest = components["schemas"]["ComponentRequest"];
export type PlanRequest = components["schemas"]["PlanRequest"];
export type PlanItem = components["schemas"]["PlanItemDto"];

export const commerceKeys = {
  components: ["admin", "components"] as const,
  plans: ["admin", "plans"] as const,
  pricing: ["admin", "pricing"] as const,
  subscriptions: ["admin", "/v1/admin/subscriptions"] as const,
  changeRequests: ["admin", "change-requests"] as const,
};

export function useComponents() {
  const { api } = useBrandex();
  return useQuery({
    queryKey: commerceKeys.components,
    queryFn: async () => required((await api.http.GET("/v1/admin/package-components")).data),
  });
}

export function usePlans() {
  const { api } = useBrandex();
  return useQuery({
    queryKey: commerceKeys.plans,
    queryFn: async () => required((await api.http.GET("/v1/admin/plans")).data),
  });
}

export function usePricing() {
  const { api } = useBrandex();
  return useQuery({
    queryKey: commerceKeys.pricing,
    queryFn: async () => required((await api.http.GET("/v1/admin/pricing-settings")).data),
  });
}

/** Only a Super admin may move the margin floor or approve a quote below it (ADR 0016 §5). */
export function useIsSuperAdmin(): boolean {
  const me = useMe();
  return me.data?.platform_roles.includes("super_admin") ?? false;
}

export function useSubscriptions() {
  const { api } = useBrandex();
  return useInfiniteQuery({
    queryKey: commerceKeys.subscriptions,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) =>
      required(
        (await api.http.GET("/v1/admin/subscriptions", {
          params: { query: { limit: PAGE, ...(pageParam === undefined ? {} : { cursor: pageParam }) } },
        })).data,
      ),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    select: (d) => d.pages.flatMap((p) => p.items),
  });
}

export function useChangeRequests() {
  const { api } = useBrandex();
  return useQuery({
    queryKey: commerceKeys.changeRequests,
    queryFn: async () => required((await api.http.GET("/v1/admin/change-requests")).data),
  });
}

// ------------------------------------------------------------------ CMS (F-03)

export type SiteSections = components["schemas"]["SiteSectionsDto"];
export type SiteEditor = components["schemas"]["SiteEditorDto"];
export type SiteLocale = "en" | "bn";

export const cmsKeys = { site: (locale: SiteLocale) => ["admin", "cms", "site", locale] as const };

export function useSiteEditor(locale: SiteLocale) {
  const { api } = useBrandex();
  return useQuery({
    queryKey: cmsKeys.site(locale),
    queryFn: async () => required((await api.http.GET("/v1/admin/cms/site", { params: { query: { locale } } })).data),
  });
}

// ------------------------------------------------------- websites (F-21)

export type AdminWebsite = components["schemas"]["AdminWebsiteDto"];

export const websiteKeys = { list: ["admin", "/v1/admin/websites"] as const };

/** Doctor websites with domain and live state (`platform.websites.manage`). */
export function useWebsites() {
  const { api } = useBrandex();
  return useInfiniteQuery({
    queryKey: websiteKeys.list,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data } = await api.http.GET("/v1/admin/websites", {
        params: { query: { limit: 50, ...(pageParam === undefined ? {} : { cursor: pageParam }) } },
      });
      return required(data);
    },
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    select: (d) => d.pages.flatMap((p) => p.items),
  });
}

// ------------------------------------------------------------ articles (F-32)

export type AdminArticle = components["schemas"]["ArticleSummaryDto"];
export type ArticleStatus = components["schemas"]["ArticleStatusDto"];

/** Every doctor's articles, newest first (`platform.articles.manage`). */
export function useAdminArticles(status: "" | ArticleStatus, search: string) {
  const { api } = useBrandex();
  return useQuery<AdminArticle[]>({
    queryKey: ["admin", "articles", status, search],
    queryFn: async () => {
      const query = { ...(status === "" ? {} : { status }), ...(search === "" ? {} : { q: search }) };
      return required((await api.http.GET("/v1/admin/articles", { params: { query } })).data).items;
    },
  });
}
